import { computed, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CargaMasivaResumen,
  FilaResultado
} from 'src/app/domain/model/carga-masiva/carga-masiva';
import { FileSelectedEvent } from '../shared/file-uploader/file-uploader.component';

export type CargaMasivaTab = 'todos' | 'exitosos' | 'errores';

interface ProcesarArchivoConfig {
  logTag: string;
  errorFallbackMessage: string;
}

/**
 * Estado y comportamiento compartido para dialogos de carga masiva.
 */
export abstract class BaseCargaMasivaDialog {
  /** Estado actual del dialogo */
  estado = signal<'cargar' | 'procesando'>('cargar');

  /** Indica si esta procesando */
  procesando = signal(false);

  /** Archivo seleccionado */
  archivoSeleccionado = signal<File | null>(null);

  /** Resultado del backend */
  resultado = signal<CargaMasivaResumen | null>(null);

  /** Error del procesamiento */
  error = signal<string | null>(null);

  /** Tab activo en la vista de resultados */
  tabActivo = signal<CargaMasivaTab>('todos');

  /** Filas a mostrar segun el tab activo */
  filasVisibles = computed((): FilaResultado[] => {
    const res = this.resultado();
    const detalles = res?.detalles ?? [];

    switch (this.tabActivo()) {
      case 'exitosos':
        return detalles.filter((f: FilaResultado) => f.exitoso);
      case 'errores':
        return detalles.filter((f: FilaResultado) => !f.exitoso);
      default:
        return detalles;
    }
  });

  /**
   * Maneja la seleccion del archivo.
   */
  onFileSelected(event: FileSelectedEvent): void {
    console.log('[CARGA-MASIVA] Archivo seleccionado:', event.fileName);
    this.archivoSeleccionado.set(event.file);
    this.error.set(null);
    this.resultado.set(null);
  }

  /**
   * Maneja la eliminacion del archivo.
   */
  onFileRemoved(): void {
    this.archivoSeleccionado.set(null);
    this.resultado.set(null);
  }

  /**
   * Maneja errores del uploader.
   */
  onFileError(errorMsg: string): void {
    this.error.set(errorMsg);
    this.archivoSeleccionado.set(null);
  }

  /**
   * Procesa el archivo con la operacion especifica del dialogo.
   */
  protected procesarArchivo(
    operacion: (archivo: File) => Observable<CargaMasivaResumen>,
    config: ProcesarArchivoConfig
  ): void {
    const archivo = this.archivoSeleccionado();

    if (!archivo) {
      this.error.set('No hay archivo seleccionado');
      return;
    }

    console.log(`[${config.logTag}] Enviando archivo al backend:`, archivo.name);

    this.estado.set('procesando');
    this.procesando.set(true);
    this.error.set(null);

    operacion(archivo).subscribe({
      next: (resumen) => {
        console.log(`[${config.logTag}] Respuesta exitosa:`, resumen);
        this.resultado.set(resumen);
        this.estado.set('cargar');
        this.procesando.set(false);
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        console.error(`[${config.logTag}] Error procesando archivo:`, err);
        this.error.set(
          err.error?.message ?? err.message ?? config.errorFallbackMessage
        );
        this.estado.set('cargar');
        this.procesando.set(false);
      }
    });
  }

  /**
   * Reinicia estado para cargar otro archivo.
   */
  protected reiniciarEstado(onAfterReset?: () => void): void {
    this.estado.set('cargar');
    this.archivoSeleccionado.set(null);
    this.resultado.set(null);
    this.error.set(null);
    this.tabActivo.set('todos');
    onAfterReset?.();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
